import { useState, useEffect } from 'react';
import { getStoredCompletion } from '../utils/modalHelpers';

/**
 * Custom hook for managing achievement and completion state
 * @param {Object} user - Current user object
 * @param {Object} userProgress - User's progress data
 * @param {boolean} showLessonModal - Whether lesson modal is open
 * @param {Function} isPathCompletedLocal - Function to check if path is completed
 * @returns {Object} Achievement state and handlers
 */
export const useAchievementState = (
  user,
  userProgress,
  showLessonModal,
  isPathCompletedLocal
) => {
  // Achievement state
  const [showAchievementView, setShowAchievementView] = useState(false);
  const [hasUserClosedAchievement, setHasUserClosedAchievement] = useState(false);
  const [hasShownAutoCompletion, setHasShownAutoCompletion] = useState(false);
  const [showAchievementConfetti, setShowAchievementConfetti] = useState(false);
  const [hasShownAchievementConfetti, setHasShownAchievementConfetti] = useState(false);

  // Window dimensions for confetti
  const [windowDimension, setWindowDimension] = useState({ width: 300, height: 300 });

  // Other UI state
  const [expandedLessons, setExpandedLessons] = useState(new Set());
  const [courseActionLoading, setCourseActionLoading] = useState(false);
  const [nextContentLoading, setNextContentLoading] = useState(false);

  // Set window dimensions for confetti
  useEffect(() => {
    setWindowDimension({ width: window.innerWidth, height: window.innerHeight });
  }, []);

  // Sync with backend progress data when it becomes available
  useEffect(() => {
    const userId = user?.id;
    const pathId = user?.path?._id;

    if (userId && pathId && userProgress?.isPathCompleted) {
      const stored = getStoredCompletion(userId, pathId);
      if (stored && !hasShownAutoCompletion) {
        // User has seen completion before, prevent auto-opening
        setHasShownAutoCompletion(true);
      }
    }
  }, [user?.id, user?.path?._id, userProgress?.isPathCompleted, hasShownAutoCompletion]);

  // Auto-open achievement view when path is completed
  useEffect(() => {
    if (userProgress?.isPathCompleted && !showAchievementView && !hasShownAutoCompletion) {
      // If path is completed and achievement view is not shown, show it (but only once automatically)
      setShowAchievementView(true);
      setHasShownAutoCompletion(true);
    }
  }, [userProgress?.isPathCompleted, showAchievementView, hasShownAutoCompletion]);

  // Show confetti when achievement view is displayed for the first time
  useEffect(() => {
    if ((showAchievementView || isPathCompletedLocal()) && !hasShownAchievementConfetti && showLessonModal) {
      setShowAchievementConfetti(true);
      setHasShownAchievementConfetti(true);

      // Auto-hide confetti after 10 seconds
      setTimeout(() => setShowAchievementConfetti(false), 10000);
    }
  }, [showAchievementView, isPathCompletedLocal, hasShownAchievementConfetti, showLessonModal]);

  // Helper function to toggle lesson accordion
  const toggleLessonAccordion = (contentId, isCompleted) => {
    if (!isCompleted) return; // Only allow expansion of completed lessons

    setExpandedLessons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(contentId)) {
        newSet.delete(contentId);
      } else {
        newSet.add(contentId);
      }
      return newSet;
    });
  };

  // Handle achievement view close
  const handleAchievementClose = () => {
    if (showAchievementView) {
      setHasUserClosedAchievement(true);
    }
    setShowAchievementView(false);
  };

  return {
    // Achievement state
    showAchievementView,
    setShowAchievementView,
    hasUserClosedAchievement,
    setHasUserClosedAchievement,
    hasShownAutoCompletion,
    setHasShownAutoCompletion,
    showAchievementConfetti,
    setShowAchievementConfetti,
    hasShownAchievementConfetti,
    setHasShownAchievementConfetti,

    // Window dimensions
    windowDimension,
    setWindowDimension,

    // UI state
    expandedLessons,
    setExpandedLessons,
    courseActionLoading,
    setCourseActionLoading,
    nextContentLoading,
    setNextContentLoading,

    // Handlers
    toggleLessonAccordion,
    handleAchievementClose
  };
};